"use client";
import React, { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { HomeWrapper } from "@/components/layouts";
import { containerWidth } from "@/components/layouts/utils";
import { Hero } from "@/components/bnp-ui";
import PriceChart from "@/components/pages/price-index/PriceChart";
import MarketAnalysisCards from "@/components/pages/price-index/MarketAnalysisCards";
import FilterControls from "@/components/pages/price-index/FilterControls";
import DataTable from "@/components/pages/price-index/DataTable";
import ExportButton from "@/components/shared/ExportButton";
import PriceIndexService from "@/services/priceIndex";
import {
  FilterParams,
  ChartDataTransformed,
  PriceIndexData,
} from "@/types/price-index";
import { ExportFormat } from "@/types/export";
import useExport from "@/hooks/useExport";
import { toast } from "react-toastify";

const PriceIndexPage: React.FC = () => {
  const [filters, setFilters] = useState<FilterParams>({
    sortBy: "date",
    sortOrder: "desc",
  });
  const [showChartArea, setShowChartArea] = useState(false);
  const { progress, exportData } = useExport();

  const {
    data: priceIndexResponse,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["price-index-data", filters],
    queryFn: () => PriceIndexService.GetPriceIndexData(filters),
    select: (response) => response.data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: locationsData, isLoading: locationsLoading } = useQuery({
    queryKey: ["price-index-locations"],
    queryFn: () => PriceIndexService.GetAvailableLocations(),
    select: (response) => response.data,
    staleTime: 10 * 60 * 1000,
  });

  const { data: propertyTypesData, isLoading: propertyTypesLoading } = useQuery(
    {
      queryKey: ["price-index-property-types"],
      queryFn: () => PriceIndexService.GetAvailablePropertyTypes(),
      select: (response) => response.data,
      staleTime: 10 * 60 * 1000,
    },
  );

  useEffect(() => {
    if (isError && error) {
      toast.error("Failed to load price index data. Please try again later.");
    }
  }, [isError, error]);

  const chartData: ChartDataTransformed[] = useMemo(() => {
    if (!priceIndexResponse?.data || priceIndexResponse.data.length === 0) {
      return [];
    }

    const dataPointsMap = new Map<string, ChartDataTransformed>();

    priceIndexResponse.data.forEach((indexData: PriceIndexData) => {
      indexData.dataPoints.forEach((point) => {
        const dateKey = point.date;
        const locationKey = `${indexData.location}_${indexData.propertyType}`;

        if (!dataPointsMap.has(dateKey)) {
          dataPointsMap.set(dateKey, { date: dateKey });
        }

        const existing = dataPointsMap.get(dateKey);
        if (existing) {
          existing[locationKey] = point.price;
        }
      });
    });

    return Array.from(dataPointsMap.values()).sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, [priceIndexResponse]);

  const handleFilterChange = (newFilters: FilterParams) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
    }));
  };

  const exportableData = useMemo(() => {
    if (!priceIndexResponse?.data || priceIndexResponse.data.length === 0) {
      return [];
    }

    return priceIndexResponse.data.flatMap((indexData: PriceIndexData) =>
      indexData.dataPoints.map((point) => ({
        Date: point.date,
        Location: indexData.location,
        PropertyType: indexData.propertyType,
        Price: point.price,
        Change: point.change || 0,
        ChangePercent: point.changePercent || 0,
        AveragePrice: indexData.averagePrice,
        MedianPrice: indexData.medianPrice,
        TotalProperties: indexData.totalProperties,
      })),
    );
  }, [priceIndexResponse]);

  const handleExportData = async (format: ExportFormat) => {
    const filename = `price-index-${new Date().toISOString().split("T")[0]}`;
    await exportData(exportableData, format, filename);
  };

  return (
    <HomeWrapper>
      <div className={containerWidth}>
        <Hero />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
          {/* Market Analysis Cards */}
          {priceIndexResponse?.metrics && (
            <MarketAnalysisCards
              metrics={priceIndexResponse.metrics}
              isLoading={isLoading}
            />
          )}

          {/* Filter Controls */}
          <FilterControls
            onFilterChange={handleFilterChange}
            availableLocations={locationsData || []}
            availablePropertyTypes={propertyTypesData || []}
            isLoading={isLoading || locationsLoading || propertyTypesLoading}
          />

          {/* Chart Section */}
          <div className="mb-8">
            <div className="bg-white rounded-xl border border-grey-3 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-main-900">
                  Price Trends
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowChartArea(!showChartArea)}
                    className="text-sm text-bnp-cyan hover:text-bnp-dark font-medium transition-colors"
                  >
                    {showChartArea ? "Line Chart" : "Area Chart"}
                  </button>
                  <ExportButton
                    data={exportableData}
                    onExport={handleExportData}
                    progress={progress}
                    variant="outline"
                    size="md"
                  />
                </div>
              </div>

              {isLoading ? (
                <div className="flex items-center justify-center h-96 bg-weak-100 rounded-xl">
                  <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-bnp-cyan"></div>
                    <p className="mt-4 text-grey-4 text-sm">
                      Loading price data...
                    </p>
                  </div>
                </div>
              ) : chartData.length > 0 ? (
                <PriceChart
                  data={chartData}
                  showArea={showChartArea}
                  enableZoom={true}
                  height={400}
                />
              ) : (
                <div className="flex items-center justify-center h-96 bg-weak-100 rounded-xl">
                  <div className="text-center">
                    <svg
                      className="w-16 h-16 mx-auto text-grey-5 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                    <p className="text-grey-4 text-sm">
                      No price data available for the selected filters
                    </p>
                    <p className="text-grey-5 text-xs mt-2">
                      Try adjusting your filter criteria
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Data Table Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-main-900">
                Detailed Index Data
              </h2>
              <p className="text-sm text-grey-4">
                {priceIndexResponse?.totalCount
                  ? `${priceIndexResponse.totalCount} results`
                  : ""}
              </p>
            </div>
            <DataTable
              data={priceIndexResponse?.data || []}
              isLoading={isLoading}
              pageSize={10}
            />
          </div>

          {/* Info Section */}
          <div className="bg-weak-100 rounded-xl p-6 border border-grey-3">
            <h3 className="text-lg font-semibold text-main-900 mb-3">
              About the Price Index
            </h3>
            <div className="text-sm text-grey-4 space-y-2">
              <p>
                The FNP Property Price Index provides comprehensive tracking of
                property prices across Nigeria. Our data is updated regularly to
                reflect current market conditions and trends.
              </p>
              <p>
                Use the filters above to explore price trends by location,
                property type, and time period. The interactive charts allow you
                to zoom in on specific date ranges and compare multiple metrics.
              </p>
              <p className="text-xs text-grey-5 mt-4">
                Data is aggregated from verified property listings and market
                surveys. All prices are displayed in Nigerian Naira (₦).
              </p>
            </div>
          </div>
        </div>
      </div>
    </HomeWrapper>
  );
};

export default PriceIndexPage;
